import { groupBy, mapValues } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  getCO2eRecord,
  getResultsById,
  getProductConversionFactors,
} from '@shared/helpers/results.helpers';
import {
  forceUpdateProductsInProject,
  getProduct,
  getProductId,
} from '@shared/helpers/product.helpers';
import {
  getAllBuildingVersions,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import {
  IProduct,
  ProductID,
  ProductRecord,
} from '@shared/models/product.model';
import {
  Baseline,
  IProductElement,
  Project,
} from '@shared/models/project.model';
import {
  Results,
  ResultsRecord,
  PartialConversionFactorQuantityRecord,
  ConversionFactorUnit,
} from '@shared/models/unit.model';
import { useProductsLookup } from '@client/store/product';
import {
  getSelectedVersion,
  useSelectedVersionProductsAndStoreProducts,
} from '@client/store/ui';
import {
  getProject,
  useUpdateProject,
} from '@client/store/project/project.hook';
import {
  getVersionById,
  updateProductInVersion,
} from '@shared/helpers/project.helpers';
import { required } from '@shared/helpers/function.helpers';
import { useProjectStateStore } from '@client/store/project/project.store';
import { useFlatSortFilteredElements } from '@client/hooks/use-filter-elements.hooks';
import { OptionallyRequired } from '@shared/models/type-helpers.model';
import { SupportedConversionUnits } from '@shared/helpers/conversion-factors.helpers';
import type { SnapshotProjectUpdate } from '@client/store/project/project.hook';

export const useGroupedProductElements = (): Record<
  ProductID,
  IProductElement[]
> => {
  const elements = useFlatSortFilteredElements();
  return useMemo(() => {
    const productElements = elements.filter(isProductElement);

    const groups = groupBy(productElements, (element) => element.product_id);

    return groups ?? {};
  }, [elements]);
};

/**
 * Get all products in the project
 * @returns
 */
const useProductsInProject = (): ProductRecord => {
  // Use this to only trigger reread of products when the version.products have changed
  const versionProducts = useProjectStateStore(({ project }) =>
    getAllBuildingVersions(project).map((v) => v.products),
  );

  return useMemo(() => {
    // Add products from selected version last to make sure they are prioritized (so we don't compare products from different versions)
    return [...versionProducts, getSelectedVersion()?.products ?? {}].reduce(
      (acc, products) => {
        return { ...acc, ...products };
      },
      {} as ProductRecord,
    );
  }, [versionProducts]);
};

/**
 * Get product from a version or from store if not found
 * @param idOrItem
 * @returns
 */
export const useProduct = <T extends boolean = true>(
  idOrItem: IProduct | IProductElement | ProductID | undefined,
  requireProduct: T = true as T,
): OptionallyRequired<IProduct | undefined, T> | undefined => {
  const products = useProductsInProject();
  const productsLookup = useProductsLookup();

  if (!idOrItem) {
    return required(undefined, requireProduct);
  }

  const id = getProductId(required(idOrItem, requireProduct));
  return required(products[id] ?? productsLookup[id], requireProduct);
};

export const useGenericProduct = ({
  generic_product_id,
}: IProductElement): IProduct | undefined =>
  useProduct(generic_product_id, false);

/**
 * Use product from selected version. Will fallback to store (or generic products lookup) if not found in selected version.
 * Causes fewer rerenders than useProduct or useProductsInProject)
 * @param id
 * @param requireProduct If true, it will throw an error if product is not found
 * @returns
 */
export const useSelectedVersionProduct = <T extends boolean = true>(
  id: ProductID,
  requireProduct: T = true as T,
): OptionallyRequired<IProduct | undefined, T> => {
  const products = useSelectedVersionProductsAndStoreProducts();
  return useMemo(
    () => getProduct(products, id, requireProduct),
    [products, id, requireProduct],
  );
};

/**
 * Get an up-to-date ConversionFactor record for the available products
 */
export function useProductConversionFactorsRecord(
  perUnit: SupportedConversionUnits | undefined,
): ResultsRecord | PartialConversionFactorQuantityRecord {
  const productsLookup = useProductsLookup();

  return useMemo(
    () =>
      mapValues(productsLookup, (product) =>
        getProductConversionFactors(product, perUnit),
      ),
    [productsLookup, perUnit],
  );
}

export const useProductConversionFactors = (
  id: ProductID,
  perUnit?: ConversionFactorUnit,
): Results => {
  return getResultsById(useProductConversionFactorsRecord(perUnit), id);
};

export const useProductCO2eRecord = (
  perUnit?: ConversionFactorUnit,
): Record<ProductID, number> => {
  const record = useProductConversionFactorsRecord(perUnit);
  return useMemo(() => getCO2eRecord(record), [record]);
};

export const useProductMaxCO2e = (perUnit?: ConversionFactorUnit): number => {
  const record = useProductCO2eRecord(perUnit);
  return useMemo(() => Math.max(...Object.values(record)), [record]);
};

export const useForceUpdateProductsInProjectCallback = (
  resetCost?: boolean,
): ((p: Project) => Project) => {
  const productsLookup = useProductsLookup();

  return useCallback(
    (project: Project) =>
      forceUpdateProductsInProject(project, productsLookup, resetCost),
    [productsLookup, resetCost],
  );
};

/**
 * Update or add a product to a version.
 * Will only updates if anything have changed
 * @returns The updated product
 */
export const useUpdateProductInVersion = (
  serverOptions?: Omit<SnapshotProjectUpdate, 'kind' | 'project'>,
): ((product: IProduct, version?: Baseline) => Promise<IProduct>) => {
  const updateProject = useUpdateProject();

  return useCallback(
    async (
      updatedProduct: IProduct,
      version: Baseline | undefined = getSelectedVersion(),
    ): Promise<IProduct> => {
      const project = getProject();

      if (!version) {
        throw new Error('No version selected');
      }

      const updatedProject = updateProductInVersion(
        project,
        version,
        updatedProduct,
      );

      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      await updateProject({
        kind: 'snapshot',
        project: updatedProject,
        ...serverOptions,
      });
      return updatedProduct;
    },
    [updateProject, serverOptions],
  );
};

/**
 * Update or add a product in all versions
 * Will only updates if anything have changed
 * @returns The updated product
 */
export const useUpdateProductInProject = () => {
  const updateProject = useUpdateProject();

  return useCallback(
    async (updatedProduct: IProduct): Promise<IProduct> => {
      let project = getProject();
      const versionIds = getAllBuildingVersions(project).map((v) => v.id);

      for (const versionId of versionIds) {
        const version = getVersionById(project, versionId);
        project = updateProductInVersion(project, version, updatedProduct);
      }

      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      await updateProject({ kind: 'snapshot', project });
      return updatedProduct;
    },
    [updateProject],
  );
};
